import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  CardPromptActionTypes,
  CardPromptAttributes,
  CardPromptBinType,
  CardPromptState,
  PromptType,
} from "./types";
import { drop, fetch, insert } from "../../repo/api";
import { produce } from "immer";

export const findMany = createAsyncThunk(
  CardPromptActionTypes.FETCH_MANY,
  async () => {
    const response = await fetch({
      id: undefined,
      params: undefined,
      nestedResourcePath: ["cardPrompts"],
    });
    return response.data;
  }
);

export const save = createAsyncThunk(
  CardPromptActionTypes.SAVE,
  async ({ id, payload }: { id?: string; payload: CardPromptAttributes }) => {
    const response = await insert({
      id,
      nestedResourcePath: ["cardPrompts"],
      data: {
        data: {
          attributes: payload,
        },
      },
    });
    if (response.data.data) {
      return response.data;
    } else {
      throw new response.data.error();
    }
  }
);

export const deleteOne = createAsyncThunk(
  CardPromptActionTypes.DELETE,
  async ({ id }: { id: string }) => {
    const response = await drop({
      id,
      nestedResourcePath: ["cardPrompts"],
    });
    return response.data;
  }
);

const initialState: CardPromptState = {
  entries: [],
  selected: undefined,
  loading: false,
};

const cardPromptSlice = createSlice({
  name: "cardPrompt",
  initialState: initialState,
  reducers: {
    selectCardPrompt: (state, action) => {
      const { cardPrompt } = action.payload;
      state.selected = cardPrompt;
    },
    addCardBin: (state, action) => {
      const { Range, Prompt } = action.payload;
      if (state.selected) {
        state.selected.attributes.Bin = produce(
          state.selected.attributes.Bin,
          (draft: CardPromptBinType[]) => {
            draft.push({ Range, Prompt });
          }
        );
      }
    },
    deleteCardBin: (state, action) => {
      const { index } = action.payload;
      if (state.selected) {
        state.selected.attributes.Bin = produce(
          state.selected.attributes.Bin,
          (draft: CardPromptBinType[]) => {
            draft.splice(index, 1);
          }
        );
      }
    },
    addPromptEntry: (state, action) => {
      const { cardBinIndex, newEntry } = action.payload;
      if (state.selected) {
        state.selected.attributes.Bin = produce(
          state.selected.attributes.Bin,
          (draft: CardPromptBinType[]) => {
            draft[cardBinIndex].Prompt.push(newEntry);
          }
        );
      }
    },
    removePromptEntry: (state, action) => {
      const { cardBinIndex, promptIndex } = action.payload;
      if (state.selected) {
        const newPrompts = [
          ...state.selected.attributes.Bin[cardBinIndex].Prompt,
        ];
        newPrompts.splice(promptIndex, 1);

        state.selected.attributes.Bin = produce(
          state.selected.attributes.Bin,
          (draft: CardPromptBinType[]) => {
            draft[cardBinIndex].Prompt = newPrompts;
          }
        );
      }
    },
    setPromptEntry: (state, action) => {
      const { prompt, cardBinIndex, promptIndex } = action.payload;
      if (state.selected) {
        state.selected.attributes.Bin = produce(
          state.selected.attributes.Bin,
          (draft: CardPromptBinType[]) => {
            draft[cardBinIndex].Prompt[promptIndex] = prompt;
          }
        );
      }
    },
    addRangeEntry: (state, action) => {
      const { cardBinIndex, newEntry } = action.payload;
      if (state.selected) {
        state.selected.attributes.Bin = produce(
          state.selected.attributes.Bin,
          (draft: CardPromptBinType[]) => {
            draft[cardBinIndex].Range.push(newEntry);
          }
        );
      }
    },
    removeRangeEntry: (state, action) => {
      const { cardBinIndex, rangeIndex } = action.payload;
      if (state.selected) {
        const newRange = [...state.selected.attributes.Bin[cardBinIndex].Range];
        newRange.splice(rangeIndex, 1);

        state.selected.attributes.Bin = produce(
          state.selected.attributes.Bin,
          (draft: CardPromptBinType[]) => {
            draft[cardBinIndex].Range = newRange;
          }
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(findMany.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(findMany.fulfilled, (state, action) => {
      state.entries = action.payload.data;
      state.loading = false;
    });
    builder.addCase(save.fulfilled, (state, action) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const cardPrompt = action.payload.data;
      if (cardPrompt) {
        const index = state.entries.findIndex(
          (entry) => entry.id === cardPrompt.id
        );
        state.entries = produce(state.entries, (draft) => {
          if (index < 0) {
            draft.push(action.payload.data);
          } else {
            draft[index] = cardPrompt;
          }
        });
      }
    });
    builder.addCase(deleteOne.fulfilled, (state, action) => {
      const { id } = action.meta.arg;
      state.entries = produce(state.entries, (draft) => {
        const index = draft.findIndex((entry) => entry.id === id);
        if (index > -1) {
          draft.splice(index, 1);
        }
      });
    });
  },
});

export default cardPromptSlice.reducer;
export const {
  selectCardPrompt,
  addCardBin,
  deleteCardBin,
  addPromptEntry,
  removePromptEntry,
  setPromptEntry,
  addRangeEntry,
  removeRangeEntry,
} = cardPromptSlice.actions;
